import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import AnimatedSection from "@/components/AnimatedSection";
import { teamStatsData, teamFunctionsData, teamValuesData } from "@/mocks/companyPageData";

export default function OurTeamPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Company" },
          { label: "Our Team" },
        ]}
      />

      <PageHero
        badge="Our Team"
        headline="The People Behind AstroJal"
        subtext="A close-knit team of engineers, designers and delivery leads who care deeply about building software that lasts."
        primaryCTA="Work With Us"
        secondaryCTA="View Open Roles"
      />

      {/* Stats */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {teamStatsData.map((stat, i) => (
              <AnimatedSection key={stat.label} delay={i * 80}>
                <div className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60 h-full">
                  <p className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F97316] font-heading">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-2">{stat.label}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* One team, many disciplines */}
      <section className="py-16 md:py-24 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                How We&apos;re Organised
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                One Team, Many Disciplines
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                AstroJal is built around focused, cross-functional teams. Each discipline owns its craft
                while staying tightly connected to the rest of the project.
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {teamFunctionsData.map((fn, i) => (
              <AnimatedSection key={fn.title} delay={i * 80}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${fn.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {fn.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{fn.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Working together */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="relative rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20software%20engineering%20team%20collaborating%20around%20a%20large%20shared%20desk%20with%20laptops%20and%20monitors%20showing%20code%2C%20warm%20natural%20lighting%2C%20clean%20premium%20office%2C%20professional%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=astrojal-our-team-collab&orientation=landscape"
                  alt="AstroJal team collaborating on a project"
                  className="w-full h-[320px] md:h-[420px] object-cover object-top"
                />
              </div>
            </AnimatedSection>
            <div>
              <AnimatedSection delay={100}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                  <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">
                    How We Work Together
                  </span>
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                  Small Teams, Big Ownership
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  Rather than spreading people thin across many projects, we build focused teams that
                  stay with a product from first idea to long-term support. That continuity is what
                  keeps quality high and communication simple.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed">
                  Every teammate has a clear role, direct access to the people making decisions, and
                  the room to do their best work.
                </p>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                What Drives Us
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                The Values We Share
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {teamValuesData.map((value, i) => (
              <AnimatedSection key={value.title} delay={i * 80}>
                <div className="group p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${value.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Want to Build With This Team?"
        text="Whether you're hiring us or joining us, we'd love to hear from you. Let's talk about what we can build together."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to Our Team"
      />
    </PageLayout>
  );
}